import fs from 'fs-extra';
import path from 'path';
import crypto from 'crypto';

// 存储接口
export interface IStorage {
  get(key: string): Promise<any>;
  set(key: string, value: any): Promise<void>;
  delete(key: string): Promise<void>;
  has(key: string): Promise<boolean>;
  list(pattern?: string): Promise<string[]>;
  clear(): Promise<void>;
}

// 文件存储实现
export class FileStorage implements IStorage {
  constructor(private basePath: string) {
    fs.ensureDirSync(this.basePath);
  }

  private getFilePath(key: string): string {
    // 将 key 转换为安全的文件名
    const safeKey = key.replace(/[^a-zA-Z0-9.-]/g, '_');
    return path.join(this.basePath, `${safeKey}.json`);
  }

  async get(key: string): Promise<any> {
    const filePath = this.getFilePath(key);
    
    if (!await fs.pathExists(filePath)) {
      return undefined;
    }
    
    try {
      const content = await fs.readFile(filePath, 'utf-8');
      return JSON.parse(content);
    } catch (error) {
      throw new Error(`Failed to read storage key "${key}": ${error.message}`);
    }
  }

  async set(key: string, value: any): Promise<void> {
    const filePath = this.getFilePath(key);
    
    try {
      await fs.writeJson(filePath, value, { spaces: 2 });
    } catch (error) {
      throw new Error(`Failed to write storage key "${key}": ${error.message}`);
    }
  }

  async delete(key: string): Promise<void> {
    const filePath = this.getFilePath(key);
    
    if (await fs.pathExists(filePath)) {
      await fs.remove(filePath);
    }
  }

  async has(key: string): Promise<boolean> {
    const filePath = this.getFilePath(key);
    return fs.pathExists(filePath);
  }

  async list(pattern?: string): Promise<string[]> {
    try {
      const files = await fs.readdir(this.basePath);
      const jsonFiles = files.filter(f => f.endsWith('.json'));
      
      let keys = jsonFiles.map(f => f.replace('.json', '').replace(/_/g, '.'));
      
      if (pattern) {
        const regex = new RegExp(pattern);
        keys = keys.filter(k => regex.test(k));
      }
      
      return keys;
    } catch (error) {
      throw new Error(`Failed to list storage keys: ${error.message}`);
    }
  }

  async clear(): Promise<void> {
    await fs.emptyDir(this.basePath);
  }
}

// 内存存储实现
export class MemoryStorage implements IStorage {
  private store = new Map<string, any>();

  async get(key: string): Promise<any> {
    return this.store.get(key);
  }

  async set(key: string, value: any): Promise<void> {
    this.store.set(key, value);
  }

  async delete(key: string): Promise<void> {
    this.store.delete(key);
  }

  async has(key: string): Promise<boolean> {
    return this.store.has(key);
  }

  async list(pattern?: string): Promise<string[]> {
    let keys = Array.from(this.store.keys());
    
    if (pattern) {
      const regex = new RegExp(pattern);
      keys = keys.filter(k => regex.test(k));
    }
    
    return keys;
  }

  async clear(): Promise<void> {
    this.store.clear();
  }
}

// 加密存储装饰器
export class EncryptedStorage implements IStorage {
  private cipher: crypto.Cipher;
  private decipher: crypto.Decipher;
  
  constructor(
    private storage: IStorage,
    private secretKey: string
  ) {
    // 确保密钥长度为 32 字节
    const key = crypto.scryptSync(secretKey, 'salt', 32);
    const iv = Buffer.alloc(16, 0); // 初始化向量
    
    this.cipher = crypto.createCipheriv('aes-256-cbc', key, iv);
    this.decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);
  }

  private encrypt(data: string): string {
    const key = crypto.scryptSync(this.secretKey, 'salt', 32);
    const iv = crypto.randomBytes(16);
    const cipher = crypto.createCipheriv('aes-256-cbc', key, iv);
    
    let encrypted = cipher.update(data, 'utf8', 'hex');
    encrypted += cipher.final('hex');
    
    return iv.toString('hex') + ':' + encrypted;
  }

  private decrypt(data: string): string {
    const parts = data.split(':');
    const iv = Buffer.from(parts[0], 'hex');
    const encrypted = parts[1];
    
    const key = crypto.scryptSync(this.secretKey, 'salt', 32);
    const decipher = crypto.createDecipheriv('aes-256-cbc', key, iv);
    
    let decrypted = decipher.update(encrypted, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    
    return decrypted;
  }

  async get(key: string): Promise<any> {
    const encryptedData = await this.storage.get(key);
    
    if (!encryptedData) {
      return undefined;
    }
    
    try {
      const decrypted = this.decrypt(encryptedData);
      return JSON.parse(decrypted);
    } catch (error) {
      throw new Error(`Failed to decrypt storage key "${key}": ${error.message}`);
    }
  }

  async set(key: string, value: any): Promise<void> {
    const data = JSON.stringify(value);
    const encrypted = this.encrypt(data);
    await this.storage.set(key, encrypted);
  }

  async delete(key: string): Promise<void> {
    await this.storage.delete(key);
  }

  async has(key: string): Promise<boolean> {
    return this.storage.has(key);
  }

  async list(pattern?: string): Promise<string[]> {
    return this.storage.list(pattern);
  }

  async clear(): Promise<void> {
    await this.storage.clear();
  }
}

// 分层存储
export class LayeredStorage implements IStorage {
  constructor(private layers: IStorage[]) {
    if (layers.length === 0) {
      throw new Error('LayeredStorage requires at least one storage layer');
    }
  }

  async get(key: string): Promise<any> {
    // 从高优先级到低优先级查找
    for (const layer of this.layers) {
      if (await layer.has(key)) {
        return layer.get(key);
      }
    }
    return undefined;
  }

  async set(key: string, value: any): Promise<void> {
    // 写入第一层（最高优先级）
    await this.layers[0].set(key, value);
  }

  async delete(key: string): Promise<void> {
    // 从所有层删除
    await Promise.all(this.layers.map(layer => layer.delete(key)));
  }

  async has(key: string): Promise<boolean> {
    for (const layer of this.layers) {
      if (await layer.has(key)) {
        return true;
      }
    }
    return false;
  }

  async list(pattern?: string): Promise<string[]> {
    const allKeys = new Set<string>();
    
    for (const layer of this.layers) {
      const keys = await layer.list(pattern);
      keys.forEach(k => allKeys.add(k));
    }
    
    return Array.from(allKeys);
  }

  async clear(): Promise<void> {
    await Promise.all(this.layers.map(layer => layer.clear()));
  }
}

// 工厂函数
export function createStorage(type: 'file' | 'memory', options?: any): IStorage {
  switch (type) {
    case 'file':
      return new FileStorage(options?.path || './.workflow/storage');
    case 'memory':
      return new MemoryStorage();
    default:
      throw new Error(`Unknown storage type: ${type}`);
  }
}